import type { PageRangeMode } from '@/types/index';

interface PageRangeCardProps {
  totalPages: number;
  mode: PageRangeMode;
  fromPage: number;
  toPage: number;
  rangeExpression: string;
  pageCount: number;
  onModeChange: (mode: PageRangeMode) => void;
  onFromPageChange: (value: number) => void;
  onToPageChange: (value: number) => void;
  onRangeExpressionChange: (value: string) => void;
  rangeError?: string | null;
}

export function PageRangeCard({
  totalPages,
  mode,
  fromPage,
  toPage,
  rangeExpression,
  pageCount,
  rangeError,
  onModeChange,
  // onFromPageChange,
  // onToPageChange,
  onRangeExpressionChange,
}: PageRangeCardProps) {
  return (
    <section className="bg-surface-lowest rounded-4 shadow-kiosk-sm p-3 p-lg-4">
      {/* Mobile header */}
      <div className="d-flex align-items-center justify-content-between mb-3 d-lg-none">
        <div className="d-flex align-items-center gap-2">
          <span className="text-label-md text-on-surface">
            Page Range
          </span>

          <span className="badge bg-surface-container text-on-surface fw-semibold">
            {pageCount} Pages
          </span>
        </div>

        <span className="text-body-sm text-on-surface-variant">
          {mode === 'all'
            ? `Pages 1 to ${totalPages}`
            : mode === 'custom'
              ? `Pages ${fromPage} to ${toPage}`
              : `${pageCount} random pages`}
        </span>
      </div>

      {/* Desktop header */}
      <div className="d-none d-lg-block mb-3">
        <h3 className="text-label-md text-on-surface fw-bold mb-1">
          Page selection range
        </h3>

        <p className="text-body-sm text-on-surface-variant mb-0">
          Choose full document or specify custom print targets
        </p>
      </div>

      {/* =====================================================
          MOBILE
      ===================================================== */}
      <div className="d-lg-none">
        <div className="d-flex gap-1 p-1 bg-surface-low rounded-4 mb-3">
          {(
            [
              {
                key: 'all',
                label: `All (1-${totalPages})`,
              },
              {
                key: 'custom',
                label: 'Custom',
              },
              {
                key: 'random',
                label: 'Random',
              },
            ] as {
              key: PageRangeMode;
              label: string;
            }[]
          ).map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`btn flex-grow-1 border-0 text-label-sm ${
                mode === opt.key
                  ? 'btn-primary fw-semibold'
                  : 'text-on-surface-variant'
              }`}
              onClick={() =>
                onModeChange(opt.key)
              }
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Custom Set */}
        {/* {mode === 'custom' && (
          <div className="bg-surface-low rounded-4 p-2 d-flex align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2 flex-grow-1">
              <span className="text-label-sm text-on-surface-variant px-1">
                From:
              </span>

              <div
                className="bg-surface-lowest rounded-3 px-2 flex-grow-1"
                style={{ height: 44 }}
              >
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  className="form-control border-0 bg-transparent text-center text-label-md h-100 p-0"
                  value={fromPage}
                  onChange={(e) =>
                    onFromPageChange(
                      Number(e.target.value),
                    )
                  }
                />
              </div>
            </div>

            <span className="text-on-surface-variant fw-bold">
              —
            </span>

            <div className="d-flex align-items-center gap-2 flex-grow-1">
              <span className="text-label-sm text-on-surface-variant px-1">
                To:
              </span>

              <div
                className="bg-surface-lowest rounded-3 px-2 flex-grow-1"
                style={{ height: 44 }}
              >
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  className="form-control border-0 bg-transparent text-center text-label-md h-100 p-0"
                  value={toPage}
                  onChange={(e) =>
                    onToPageChange(
                      Number(e.target.value),
                    )
                  }
                />
              </div>
            </div>
          </div>
        )} */}
        {mode === 'custom' && (
          <div className="bg-surface-low p-3 rounded-3">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <i className="bi bi-crop text-on-surface-variant" />

              <input
                type="text"
                inputMode="numeric"
                className={`form-control bg-surface-lowest text-label-md ${rangeError ? 'is-invalid' : ''}`}
                style={{ maxWidth: 220 }}
                placeholder="e.g. 2-5"
                value={rangeExpression}
                onChange={(e) => onRangeExpressionChange(e.target.value)}
              />

              {!rangeError && (
                <span className="text-body-sm text-on-surface-variant">
                  Active: <strong>{pageCount} printable pages</strong> selected
                </span>
              )}
            </div>

            {rangeError && (
              <div className="text-danger text-body-sm mt-2">{rangeError}</div>
            )}
          </div>
        )}

        {/* Random Pages */}
        {mode === 'random' && (
          <div className="bg-surface-low rounded-4 p-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <i className="bi bi-shuffle text-on-surface-variant" />

              <span className="text-label-sm text-on-surface-variant">
                Enter page numbers
              </span>
            </div>

            <input
              type="text"
              className="form-control bg-surface-lowest text-label-md"
              placeholder="e.g. 2, 5, 8, 13"
              value={rangeExpression}
              onChange={(e) =>
                onRangeExpressionChange(
                  e.target.value,
                )
              }
            />

            <div className="text-body-sm text-on-surface-variant mt-2">
              Active:{' '}
              <strong>
                {pageCount} printable pages
              </strong>{' '}
              selected
            </div>
          </div>
        )}
      </div>

      {/* =====================================================
          DESKTOP
      ===================================================== */}
      <div className="d-none d-lg-block">
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-2 mb-3">
          {(
            [
              {
                key: 'all',
                title: 'All Pages',
                sub: `1 to ${totalPages} (Full)`,
              },
              {
                key: 'custom',
                title: 'Custom set',
                sub: `Pages ${fromPage}-${toPage}`,
              },
              {
                key: 'random',
                title: 'Random pages',
                sub: '2, 5, 8, 13...',
              },
            ] as {
              key: PageRangeMode;
              title: string;
              sub: string;
            }[]
          ).map((opt) => (
            <div
              className="col"
              key={opt.key}
            >
              <label
                className={`d-flex align-items-center gap-3 p-3 rounded-3 tap-target ${
                  mode === opt.key
                    ? 'bg-surface-high'
                    : 'bg-surface-low'
                }`}
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="page-range"
                  className="form-check-input mt-0"
                  checked={mode === opt.key}
                  onChange={() =>
                    onModeChange(opt.key)
                  }
                />

                <div>
                  <p className="text-label-md text-on-surface fw-semibold mb-0">
                    {opt.title}
                  </p>

                  <p
                    className={`text-label-sm mb-0 ${
                      mode === opt.key
                        ? 'text-primary fw-bold'
                        : 'text-on-surface-variant'
                    }`}
                  >
                    {opt.sub}
                  </p>
                </div>
              </label>
            </div>
          ))}
        </div>

        {/* Custom Set */}
        {mode === 'custom' && (
          <div className="d-flex align-items-center gap-3 bg-surface-low p-3 rounded-3 flex-wrap">
            <i className="bi bi-crop text-on-surface-variant" />

            <input
              type="text"
              className="form-control bg-surface-lowest text-label-md"
              style={{ maxWidth: 220 }}
              placeholder="e.g. 1-4, 7, 9"
              value={rangeExpression}
              onChange={(e) =>
                onRangeExpressionChange(
                  e.target.value,
                )
              }
            />

            <span className="text-body-sm text-on-surface-variant">
              Active:{' '}
              <strong>
                {pageCount} printable pages
              </strong>{' '}
              selected
            </span>
          </div>
        )}

        {mode === 'custom' && rangeError && (
          <div className="text-danger text-body-sm mt-2">{rangeError}</div>
        )}

        {/* Random Pages */}
        {mode === 'random' && (
          <div className="d-flex align-items-center gap-3 bg-surface-low p-3 rounded-3 flex-wrap">
            <i className="bi bi-shuffle text-on-surface-variant" />

            <input
              type="text"
              className="form-control bg-surface-lowest text-label-md"
              style={{ maxWidth: 300 }}
              placeholder="e.g. 2, 5, 8, 13"
              value={rangeExpression}
              onChange={(e) =>
                onRangeExpressionChange(
                  e.target.value,
                )
              }
            />

            <span className="text-body-sm text-on-surface-variant">
              Active:{' '}
              <strong>
                {pageCount} printable pages
              </strong>{' '}
              selected
            </span>
          </div>
        )}
      </div>
    </section>
  );
}